import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Mail, User, CheckCircle } from 'lucide-react';

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  bookingId?: string;
}

interface BookingFormData {
  name: string;
  email: string;
  title: string;
  description: string;
  meetingType: 'video' | 'phone' | 'in-person';
  selectedSlot?: TimeSlot;
}

export const ScheduleLink: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    email: '',
    title: '',
    description: '',
    meetingType: 'video'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  useEffect(() => {
    loadAvailableSlots();
  }, [selectedDate]);

  const loadAvailableSlots = async () => {
    const mockSlots: TimeSlot[] = [
      { start: '09:00', end: '10:00', available: true },
      { start: '10:00', end: '11:00', available: false, bookingId: 'booking-001' },
      { start: '11:00', end: '12:00', available: true },
      { start: '14:00', end: '15:00', available: true },
      { start: '15:00', end: '16:00', available: true },
      { start: '16:00', end: '17:00', available: false, bookingId: 'booking-002' }
    ];

    setAvailableSlots(mockSlots);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const handleBooking = async () => {
    if (!selectedSlot || !formData.name || !formData.email) {
      alert('Please fill in all required fields and select a time slot.');
      return;
    }

    setIsLoading(true);

    try {
      const bookingData = {
        ...formData,
        selectedSlot,
        startTime: `${selectedDate.toISOString().split('T')[0]}T${selectedSlot.start}:00`,
        endTime: `${selectedDate.toISOString().split('T')[0]}T${selectedSlot.end}:00`,
        publicToken: window.location.pathname.split('/').pop()
      };

      console.log('Creating public booking:', bookingData);
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setIsBooked(true);
    } catch (error) {
      console.error('Booking failed:', error);
      alert('Booking failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isBooked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-800 border border-slate-700 p-8 rounded-xl max-w-md w-full text-center"
        >
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
          <p className="text-slate-300 mb-4">
            Your meeting has been scheduled for {selectedDate.toLocaleDateString()} at {selectedSlot?.start} - {selectedSlot?.end}.
          </p>
          <p className="text-sm text-slate-400">
            You will receive a confirmation email shortly with meeting details.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Schedule a Meeting</h1>
          <p className="text-slate-300">Select a time that works for you</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Date and Time Selection */}
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Select Date & Time</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">Available Times</label>
              <div className="grid grid-cols-2 gap-3">
                {availableSlots.map((slot, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleSlotSelect(slot)}
                    whileHover={{ scale: slot.available ? 1.02 : 1 }}
                    whileTap={{ scale: slot.available ? 0.98 : 1 }}
                    disabled={!slot.available}
                    className={`p-3 rounded-lg text-center transition-all duration-200 ${
                      selectedSlot === slot
                        ? 'bg-blue-600 text-white border-2 border-blue-400'
                        : slot.available
                        ? 'bg-slate-700 text-white hover:bg-slate-600 border border-slate-600'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                    }`}
                  >
                    <div className="text-sm font-medium">
                      {slot.start} - {slot.end}
                    </div>
                    <div className="text-xs mt-1">
                      {slot.available ? (
                        selectedSlot === slot ? 'Selected' : 'Available'
                      ) : (
                        'Booked'
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Your Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@company.com"
                  className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Meeting Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief meeting title"
                  className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What would you like to discuss?"
                  rows={3}
                  className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Meeting Type</label>
                <select
                  value={formData.meetingType}
                  onChange={(e) => setFormData({ ...formData, meetingType: e.target.value as 'video' | 'phone' | 'in-person' })}
                  className="w-full p-3 rounded-lg bg-slate-700 border border-slate-600 text-white"
                >
                  <option value="video">Video Call</option>
                  <option value="phone">Phone Call</option>
                  <option value="in-person">In Person</option>
                </select>
              </div>
            </div>

            <motion.button
              onClick={handleBooking}
              disabled={!selectedSlot || !formData.name || !formData.email || isLoading}
              whileHover={{ scale: !selectedSlot || !formData.name || !formData.email || isLoading ? 1 : 1.02 }}
              whileTap={{ scale: !selectedSlot || !formData.name || !formData.email || isLoading ? 1 : 0.98 }}
              className={`w-full mt-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedSlot && formData.name && formData.email && !isLoading
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Booking...
                </div>
              ) : (
                'Confirm Booking'
              )}
            </motion.button>

            {selectedSlot && (
              <div className="mt-4 p-3 bg-slate-700 rounded-lg">
                <div className="flex items-center text-sm text-slate-300">
                  <Clock className="w-4 h-4 mr-2" />
                  Selected: {selectedDate.toLocaleDateString()} at {selectedSlot.start} - {selectedSlot.end}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleLink;
